import React, { useState, useEffect } from 'react';
import { X, Printer, UtensilsCrossed, Loader2 } from 'lucide-react';
import { DEFAULT_PRINTER_CONFIG, PRINT_SETTINGS } from '../../config/printerConfig';
import printService from '../../services/printService';
import { toast } from 'react-hot-toast';
import receiptConfigurationService from '../../services/receiptConfigurationService';
import branchService from '../../services/branchService';
import { generateKOTContent, DEFAULT_RECEIPT_CONFIG } from '../../utils/receiptFormatter';

/**
 * QR KOT Preview — Lightweight KOT receipt modal for QR orders.
 * Reuses the same receipt layout & print logic as POS KOTReceiptDisplay
 * but without CartContext dependency.
 *
 * Props:
 *   order    – the approved QrOrderDTO (with items[].kitchenStationName)
 *   onClose  – close callback
 */
export default function QRKOTPreview({ order, onClose }) {
  const [receiptConfig, setReceiptConfig] = useState(DEFAULT_RECEIPT_CONFIG);
  const [branchDetails, setBranchDetails] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const config = await receiptConfigurationService.getDefault('KOT');
        if (config) setReceiptConfig(config);
      } catch {}
    };
    const fetchBranch = async () => {
      try {
        const branch = await branchService.getCurrentBranch();
        if (branch) setBranchDetails(branchService.formatBranchForReceipt(branch));
      } catch {}
    };
    fetchConfig();
    fetchBranch();
  }, []);

  // Group items by kitchen station
  const groupItemsByStation = (items) => {
    const grouped = {};
    (items || []).forEach((item) => {
      const station = item.kitchenStationName || 'MAIN KITCHEN';
      if (!grouped[station]) grouped[station] = [];
      grouped[station].push(item);
    });
    return grouped;
  };

  const stationGroups = groupItemsByStation(order?.items);
  const stations = Object.keys(stationGroups);

  const timestamp = new Date().toLocaleString('en-IN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const PRINTER_CONFIG = DEFAULT_PRINTER_CONFIG;
  const kotNumber = order?.kotNumber || 'N/A';
  const tableName = order?.tableName || 'N/A';
  const tableSplit = order?.tableSplit || 'A';

  // Direct print to thermal printers via backend
  const handleDirectPrint = async () => {
    try {
      const printPromises = stations.map(async (station) => {
        const stationItems = stationGroups[station];

        const kotPrintData = {
          restaurantName: branchDetails?.name || 'MAVERICK DINE',
          kotNumber,
          tableName,
          tableSplit,
          pax: null,
          stationName: station,
          waiterName: null,
          timestamp,
          items: stationItems.map((item) => ({
            name: item.itemName,
            quantity: item.quantity,
            customization: null,
          })),
          chefNotes: order?.notes,
          allergies: null,
        };

        const kotResult = generateKOTContent(receiptConfig, kotPrintData);

        const printPayload = {
          formattedContent: kotResult.content,
          printType: 'KOT',
          kitchenStationName: station,
          content: {
            kotNumber: kotPrintData.kotNumber,
            tableName: kotPrintData.tableName,
            tableSplit: kotPrintData.tableSplit,
            stationName: station,
            timestamp: kotPrintData.timestamp,
            items: kotPrintData.items,
            chefNotes: kotPrintData.chefNotes,
          },
        };

        return printService.printKOTToStation(station, printPayload);
      });

      const results = await Promise.all(printPromises);
      const allSuccess = results.every((r) => r?.success);

      if (allSuccess) {
        toast.success(
          `KOTs printed to ${stations.length} station${stations.length > 1 ? 's' : ''} successfully!`,
          { duration: 2000 }
        );
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  // Browser-based print fallback
  const handleBrowserPrint = () => {
    const receiptsHTML = stations
      .map((station) => {
        const items = stationGroups[station];
        return `
        <div class="kot-receipt" style="page-break-after: always;">
          <div class="header">
            <div class="company-name">${branchDetails?.name || 'MAVERICK DINE'}</div>
            <div class="company-subtitle">Kitchen Order Ticket</div>
            <div class="station-badge-container">
              <span class="station-badge">${station}</span>
            </div>
            <div class="receipt-number">#${kotNumber}</div>
          </div>
          <div class="info-section">
            <div class="info-row">
              <span class="font-bold">Table:</span>
              <span class="font-bold">${tableName} (${tableSplit})</span>
            </div>
            ${order?.customerName ? `<div class="info-row"><span>Customer:</span><span>${order.customerName}</span></div>` : ''}
            <div class="info-row text-xs">
              <span>Date/Time:</span>
              <span>${timestamp}</span>
            </div>
          </div>
          <div class="items-header"><span>Item</span><span>Qty</span></div>
          ${items
            .map(
              (item) => `
            <div class="item-row">
              <div class="item-main">
                <span class="item-name">${item.itemName}${item.variantName ? ` (${item.variantName})` : ''}</span>
                <span class="item-qty">${item.quantity}</span>
              </div>
              ${item.specialInstructions ? `<div class="customization-details"><div class="custom-instructions">${item.specialInstructions}</div></div>` : ''}
            </div>`
            )
            .join('')}
          ${order?.notes ? `<div class="chef-notes"><div class="notes-label">NOTES:</div><div class="notes-text">${order.notes}</div></div>` : ''}
          <div class="footer">
            <div class="text-xs">Total Items: <span class="font-bold">${items.reduce((s, i) => s + i.quantity, 0)}</span></div>
          </div>
        </div>`;
      })
      .join('');

    const printWindow = window.open('', '', 'width=800,height=600');
    if (!printWindow) {
      toast.error('Popup blocked! Please allow popups for this site.');
      return;
    }

    printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>KOT ${kotNumber} - ${tableName}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box}
      @media screen{body{font-family:'Courier New',monospace;background:#f5f5f5;padding:20px}.kot-receipt{background:white;width:${PRINTER_CONFIG.width};max-width:100%;padding:${PRINTER_CONFIG.padding};margin-bottom:20px;box-shadow:0 2px 10px rgba(0,0,0,.1);border:1px solid #ddd}}
      @media print{@page{size:${PRINTER_CONFIG.width} auto;margin:0}body{margin:0;padding:0;width:${PRINTER_CONFIG.width};background:white}.kot-receipt{width:100%;padding:${PRINTER_CONFIG.padding};${PRINT_SETTINGS.enableAutoCut ? `padding-bottom:${PRINT_SETTINGS.bottomMargin};` : ''}page-break-after:always}.kot-receipt:last-child{page-break-after:auto}.item-row{page-break-inside:avoid}}
      body{font-family:'Courier New',monospace;font-size:${PRINTER_CONFIG.fontSize};line-height:${PRINTER_CONFIG.lineHeight};color:#000}
      .text-center{text-align:center}.text-xs{font-size:.8em}.font-bold{font-weight:bold}
      .header{text-align:center;margin-bottom:1em;padding-bottom:1em;border-bottom:2px dashed #000}
      .company-name{font-size:1.4em;font-weight:bold;margin-bottom:.25em;letter-spacing:.05em}
      .company-subtitle{font-size:.8em;margin-bottom:.5em}
      .station-badge-container{margin:.75em 0}
      .station-badge{background:#000;color:#fff;padding:.5em 1.5em;display:inline-block;font-weight:bold;font-size:.9em}
      .receipt-number{font-size:.8em;margin-top:.5em}
      .info-section{margin-bottom:1em;padding-bottom:.75em;border-bottom:1px solid #000}
      .info-row{display:flex;justify-content:space-between;margin-bottom:.25em;font-size:.9em}
      .items-header{display:flex;justify-content:space-between;font-weight:bold;margin-bottom:.5em;padding-bottom:.5em;border-bottom:1px solid #000;font-size:.9em}
      .items-header span:nth-child(1){flex:1}.items-header span:nth-child(2){width:3em;text-align:center}
      .item-row{margin-bottom:.75em}
      .item-main{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:.2em}
      .item-name{flex:1;font-weight:bold}.item-qty{width:3em;text-align:center;font-weight:bold}
      .customization-details{font-size:.8em;color:#333;margin-left:.5em;margin-top:.25em}
      .custom-instructions{font-weight:bold;color:#b45309;margin-top:.25em}
      .chef-notes{background:#fef3c7;border:1px solid #d97706;padding:.5em;margin:.75em 0;border-radius:3px}
      .notes-label{font-size:.8em;font-weight:bold;color:#92400e;margin-bottom:.25em}
      .notes-text{font-size:.8em;color:#92400e}
      .footer{border-top:2px dashed #000;padding-top:.75em;margin-top:1em;text-align:center}
    </style></head><body>${receiptsHTML}
    <script>window.onload=function(){setTimeout(function(){window.print()},${PRINT_SETTINGS.autoPrintDelay})};${PRINT_SETTINGS.autoCloseAfterPrint ? `window.onafterprint=function(){setTimeout(function(){window.close()},${PRINT_SETTINGS.autoCloseDelay})};` : ''}</script>
    </body></html>`);
    printWindow.document.close();
  };

  const handlePrint = async () => {
    setIsPrinting(true);
    try {
      const success = await handleDirectPrint();
      if (success) {
        onClose();
        return;
      }
      handleBrowserPrint();
      onClose();
    } finally {
      setIsPrinting(false);
    }
  };

  if (!order) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-700 to-teal-800 text-white p-3 flex justify-between items-center flex-shrink-0">
          <div className="flex items-center gap-2">
            <UtensilsCrossed size={18} />
            <h3 className="text-base font-bold">Kitchen Order Tickets</h3>
            <span className="text-xs opacity-80 ml-2">QR Order #{order.id}</span>
          </div>
          <button
            onClick={onClose}
            disabled={isPrinting}
            className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Receipts */}
        <div className="flex-1 p-4 bg-slate-100 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {stations.map((station, idx) => {
              const items = stationGroups[station];
              return (
                <div
                  key={idx}
                  className="bg-white p-4 rounded-lg font-mono text-sm border border-slate-200 shadow-md"
                >
                  {/* Receipt Header */}
                  <div className="text-center border-b-2 border-dashed border-slate-300 pb-4 mb-4">
                    {receiptConfig.showRestaurantName && (
                      <h1 className="text-xl font-bold text-slate-900">
                        {branchDetails?.name || 'MAVERICK DINE'}
                      </h1>
                    )}
                    <p className="text-xs text-slate-600">Kitchen Order Ticket</p>
                    {receiptConfig.showKitchenStation && (
                      <div className="my-3">
                        <span className="bg-slate-900 text-white px-4 py-2 text-sm font-bold inline-block">
                          {station}
                        </span>
                      </div>
                    )}
                    {receiptConfig.showOrderNumber && (
                      <p className="text-xs text-slate-600 mt-2">#{kotNumber}</p>
                    )}
                  </div>

                  {/* Info */}
                  <div className="border-b border-slate-300 pb-3 mb-3">
                    {receiptConfig.showTableNumber && (
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-semibold text-slate-900">Table:</span>
                        <span className="font-bold text-slate-900">
                          {tableName} ({tableSplit})
                        </span>
                      </div>
                    )}
                    {order.customerName && (
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-semibold text-slate-900">Customer:</span>
                        <span className="text-slate-700">{order.customerName}</span>
                      </div>
                    )}
                    {receiptConfig.showTimestamp && (
                      <div className="flex justify-between text-xs text-slate-600">
                        <span>Date/Time:</span>
                        <span>{timestamp}</span>
                      </div>
                    )}
                  </div>

                  {/* Items */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs font-bold text-slate-900 mb-2 pb-2 border-b border-slate-300">
                      <span>Item</span>
                      <span>Qty</span>
                    </div>
                    {items.map((item, itemIdx) => (
                      <div key={itemIdx} className="mb-3">
                        <div className="flex justify-between text-xs text-slate-900">
                          <div className="flex-1">
                            <div className="font-semibold">{item.itemName}</div>
                            {item.variantName && (
                              <div className="text-[10px] text-slate-500">
                                {item.variantName}
                              </div>
                            )}
                            {item.specialInstructions && (
                              <div className="font-semibold text-amber-900 mt-1 text-[10px]">
                                {item.specialInstructions}
                              </div>
                            )}
                          </div>
                          <span className="w-12 text-center font-bold">{item.quantity}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Notes */}
                  {order.notes && (
                    <div className="bg-amber-50 border border-amber-300 rounded p-3 mb-3">
                      <div className="text-xs font-bold text-amber-900 mb-1">NOTES:</div>
                      <div className="text-xs text-amber-900">{order.notes}</div>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="border-t-2 border-dashed border-slate-300 pt-3 text-center">
                    <div className="text-xs text-slate-600">
                      Total Items:{' '}
                      <span className="font-bold text-slate-900">
                        {items.reduce((s, i) => s + i.quantity, 0)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white border-t-2 border-slate-200 p-3 flex gap-2 flex-shrink-0">
          <button
            onClick={handlePrint}
            disabled={isPrinting}
            className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-teal-700 text-white rounded-lg text-sm font-medium hover:bg-teal-800 transition shadow-md disabled:opacity-70"
          >
            {isPrinting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Printing...
              </>
            ) : (
              <>
                <Printer size={16} />
                Print All KOTs
              </>
            )}
          </button>
          <button
            onClick={onClose}
            disabled={isPrinting}
            className="flex-1 px-5 py-2.5 bg-white border-2 border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition shadow-md disabled:opacity-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
